/* global toastr */
import React from 'react'
import { wrapView, request } from '../smart_bps/lib/utils'
import { stateToPropsSigur } from '../lib/stateToPropsSigur'
import { PROJECT_NAME } from './constants'
import { SigurModelView } from '../lib/SigurModelView'
import { SigurFilesBlock } from '../lib/SigurFilesBlock'
import { store } from '../index'
import { Link } from 'react-router-dom'
import { Panel } from '../smart_bps/lib/components'
import AttrInput from '../smart_bps/lib/attr_input'
import { RequiredFieldPopover } from '../lib/RequiredFieldPopover'
import { AccessObjectsModalSearch } from './AccessObjectsModalSearch'

class ModelView extends SigurModelView {
  constructor(props) {
    super(props)
    this.state = {
      ...this.state,
      document_dates: [],
      blacklistHistoryItems: [],
      data_actualization_mode: false,
    }
    this.docsTabRef = React.createRef()
    this.openBlacklistModal = this.openBlacklistModal.bind(this)
    this.closeBlacklistModal = this.closeBlacklistModal.bind(this)
    this.saveBlacklistModal = this.saveBlacklistModal.bind(this)
    this.openBlacklistHistory = this.openBlacklistHistory.bind(this)
    this.closeBlacklistHistory = this.closeBlacklistHistory.bind(this)
    this.startDataActualization = this.startDataActualization.bind(this)
    this.exitDataActualization = this.exitDataActualization.bind(this)
    this.saveObjectWithEditAllReset = this.saveObjectWithEditAllReset.bind(this)
    this.loadDocumentDates = this.loadDocumentDates.bind(this)
    this.onDocumentDateSaved = this.onDocumentDateSaved.bind(this)
    this.onFileDeleted = this.onFileDeleted.bind(this)
    this.loadBlacklistHistory = this.loadBlacklistHistory.bind(this)
  }

  /** ID карточки: после создания в state уже есть id, в URL — ещё 0 до редиректа. */
  get resolvedObjectId() {
    const stateId = this.state && this.state.object_id
    if (stateId && stateId !== 0) return stateId
    return this.object_id
  }

  loadDocumentDates() {
    const objectId = this.resolvedObjectId
    if (!objectId || objectId === 0) return
    request({
      method: 'get',
      url: `/api/${PROJECT_NAME}/${objectId}/document_dates`,
      success: (data) => {
        this.setState({ document_dates: data.items || [] })
      },
    })
  }

  componentDidMount() {
    super.componentDidMount?.()
    this.maybeLoadBlacklistHistory()
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate?.(prevProps, prevState)
    const prevId = (prevState && prevState.object_id) || 0
    const currId = this.resolvedObjectId
    if (currId && currId !== 0 && currId !== prevId) {
      this.maybeLoadBlacklistHistory()
    }
  }

  maybeLoadBlacklistHistory() {
    const id = this.resolvedObjectId
    if (!id || id === 0) return
    const fd = this.state.form_data || {}
    if (fd.blacklist || this.state.has_blacklist_history) {
      this.loadBlacklistHistory(id)
    }
  }

  onDocumentDateSaved(item) {
    this.setState((s) => {
      const existing = s.document_dates || []
      const idx = existing.findIndex((d) => d.doc_type_id === item.doc_type_id)
      if (idx >= 0) {
        const next = [...existing]
        next[idx] = item
        return { document_dates: next }
      }
      return { document_dates: [...existing, item] }
    })
  }

  onFileDeleted(fileId) {
    this.setState((s) => ({
      files: (s.files || []).filter((f) => f.id !== fileId),
    }))
  }

  get isApplicant() {
    return Boolean(this.props.user?.roles?.includes('applicant'))
  }

  /** Кнопка «Действия»: администратор или заявитель на этапе «Действующий». */
  get canShowDocumentActions() {
    const roles = this.props.user?.roles || []
    const isAllowed = roles.includes('admin') || roles.includes('applicant')
    const state = this.state.state || 'draft'
    const objectId = this.resolvedObjectId
    return isAllowed && state === 'active' && objectId && objectId !== 0
  }

  /** Блок файлов: на «Действующий»/«Архив» только в режиме актуализации. */
  get filesBlockViewOnly() {
    const state = this.state.state || 'draft'
    if (['draft', 'active'].includes(state)) return false
    return true
  }

  get filesBlockCanDelete() {
    if (this.state.state === 'active') return true
    if (this.state.data_actualization_mode && (this.isAdmin || this.isApplicant)) {
      return true
    }
    return this.isAdmin || this.isResponsible
  }

  getEffectiveRequiredFields() {
    const base = this.state.required_fields || this.props.required_fields || []
    if (!this.state.data_actualization_mode) return base
    return [...new Set([...base, 'passport', 'position'])]
  }

  startDataActualization() {
    this.setState((prev) => {
      const baseRequired = prev.required_fields || this.props.required_fields || []
      return {
        data_actualization_mode: true,
        required_fields: [...new Set([...baseRequired, 'passport', 'position'])],
        form_access: {
          ...(prev.form_access || {}),
          passport: 'edit',
          position: 'edit',
        },
      }
    })
    toastr.info('Редактирование полей и документов включено до сохранения карточки')
  }

  /** Выход без сохранения: перезагрузка карточки с сервера, сброс прав и черновых правок. */
  exitDataActualization() {
    if (!this.state.data_actualization_mode) return
    this.setState({ data_actualization_mode: false, form_errors: undefined })
    this.getDocument(false)
    toastr.info('Режим актуализации данных отключён')
  }

  /** Общая обработка ответа update_document: дубликат организация+паспорт — тост со ссылкой (escapeHtml: false). */
  documentToState(data, cb) {
    super.documentToState(data, () => {
      this.setState({ data_actualization_mode: false }, () => {
        if (cb) cb()
      })
    })
    const id = data && data.id
    if (id && id !== 0 && (data.has_blacklist_history || data.form_data?.blacklist)) {
      this.loadBlacklistHistory(id)
    }
  }

  saveObjectWithEditAllReset() {
    const that = this
    that.savePendingFileDates().then((result) => {
      const isSavedFiles = typeof result === 'object' ? result.success : result
      const toastrMsg = result?.toastrMsg
      let new_data = that.state.form_data

      if (that.state.edit_all_mode && that.state.form_data_initial) {
        new_data = Object.fromEntries(
          Object.entries({ ...that.state.form_data_initial, ...new_data }).map(([k, v]) => [
            k,
            v === undefined ? null : v,
          ])
        )
      }

      if (that.state.data_actualization_mode) {
        new_data = { ...new_data, _data_actualization: true }
      }

      request({
        method: 'post',
        url: `/api/${that.project_name}/${that.object_id}/update_document`,
        data: {
          new_data,
          old_data: that.state.form_data_initial,
          force_edit_all: !!that.state.edit_all_mode,
        },
        success: (data) => {
          store.dispatch({ type: `${that.project_name}_list_clean` })
          if (data.document && isSavedFiles && !data.errors) {
            that.handleUpdateDocumentSuccess(data)
            that.setState({ edit_all_mode: false })
          } else if (data.errors || !isSavedFiles) {
            that.setState({ form_errors: data.errors })
            let customErrors = []
            if (data?.errors?._document_dates_error) {
              customErrors.push(data.errors._document_dates_error)
            }
            if (toastrMsg) customErrors.push(toastrMsg)
            if (!customErrors.length) {
              toastr.warning(
                'Изменения не были сохранены. Нужно исправить ошибки в форме документа',
                'Внимание'
              )
            }
            customErrors.forEach((msg) => {
              toastr.warning(
                msg || 'Изменения не были сохранены. Нужно исправить ошибки в форме документа',
                'Внимание'
              )
            })
            if (data.errors) {
              that.handleUpdateDocumentSuccess({ errors: data.errors, duplicate_access_object_id: data.duplicate_access_object_id })
            }
          }
        },
      })
    })
  }

  handleUpdateDocumentSuccess(data) {
    const that = this
    if (data.document && !data.errors) {
      that.documentToState(data.document)
      toastr.success('Изменения сохранены')
      return
    }
    if (data.errors) {
      that.setState({ form_errors: data.errors })
      if (data.duplicate_access_object_id) {
        const id = data.duplicate_access_object_id
        const href = `#/${PROJECT_NAME}/${id}`
        const msg =
          `Карточка этого сотрудника уже существует. Хотите <a href="${href}" data-toastr-action="go-duplicate" class="fw-semibold" style="color:#ea580c;text-decoration:underline;text-decoration-color:#ea580c">перейти</a> в неё?`
        const toast = toastr.warning(msg, 'Карточка уже существует', {
          escapeHtml: false,
          timeOut: 0,
          extendedTimeOut: 0,
          closeButton: true,
          tapToDismiss: false,
        })
        if (toast && toast.on) {
          toast.on('click', 'a[data-toastr-action="go-duplicate"]', () => {
            toastr.clear(toast, { force: true })
          })
        }
      } else {
        toastr.warning(
          data && data.errors && typeof data.errors === 'object' && data.errors._document_dates_error
            ? data.errors._document_dates_error
            : 'Изменения не были сохранены. Нужно исправить ошибки в форме документа',
          'Внимание'
        )
      }
    }
  }

  getDeleteConfirmTitle() {
    return 'Удаление объекта доступа'
  }

  openBlacklistModal(newValue) {
    this.setState({
      showBlacklistModal: true,
      blacklistModalValue: newValue,
      blacklistModalReason: '',
      blacklistModalError: null,
    })
  }

  closeBlacklistModal() {
    this.setState({ showBlacklistModal: false })
  }

  saveBlacklistModal() {
    const reason = (this.state.blacklistModalReason || '').trim()
    if (!reason) {
      this.setState({ blacklistModalError: 'Заполните причину' })
      return
    }
    const that = this
    request({
      method: 'post',
      url: `/api/${PROJECT_NAME}/${this.object_id}/blacklist`,
      data: { blacklist: this.state.blacklistModalValue, reason },
      success: () => {
        that.setState({
          form_data: { ...that.state.form_data, blacklist: that.state.blacklistModalValue },
          has_blacklist_history: true,
        })
        this.closeBlacklistModal()
        toastr.success('Значение сохранено')
        this.loadBlacklistHistory()
      },
      error: (err) => {
        const msg = err && err.detail ? (typeof err.detail === 'string' ? err.detail : err.detail.msg || JSON.stringify(err.detail)) : 'Ошибка сохранения'
        that.setState({ blacklistModalError: msg })
      },
    })
  }

  loadBlacklistHistory(objectId) {
    const id = objectId ?? this.resolvedObjectId
    if (!id || id === 0) return
    const that = this
    request({
      method: 'get',
      url: `/api/${PROJECT_NAME}/${id}/blacklist_history`,
      success: (data) => {
        that.setState({ blacklistHistoryItems: data.items || [] })
      },
      error: () => {
        that.setState({ blacklistHistoryItems: [] })
        toastr.error('Не удалось загрузить историю')
      },
    })
  }

  openBlacklistHistory() {
    this.setState({ showBlacklistHistoryModal: true })
  }

  closeBlacklistHistory() {
    this.setState({ showBlacklistHistoryModal: false })
  }

  // Скрываем кнопку "Создать" в карточке
  renderTopPanelLeftItems() {
    return []
  }

  // Панель информации справа
  renderInfo() {
    const state = this.state.state || 'draft'
    const { blacklistHistoryItems, granted_access_id, granted_access_title, responsible_id } = this.state
    // Получаем название этапа
    const stateTitle = this.getChoice(`bps_${PROJECT_NAME}_state`, state) || state

    const isBlacklist = Boolean(this.state.form_data?.blacklist)
    const latestBlacklistEntry = (blacklistHistoryItems || []).find((row) => row.in_blacklist)
    const blacklistReason = latestBlacklistEntry && latestBlacklistEntry.reason
      ? latestBlacklistEntry.reason
      : null

    // Получаем имя ответственного
    const responsibleName = this.getChoice('User', responsible_id) || '---'
    return (
      <Panel header="Основное" key="info_panel">
        {isBlacklist && (
          <div className="sigur-info-block__alert alert alert-danger d-flex align-items-center py-2 px-2 small mt-1 mb-2" role="alert">
            <i className="ti ti-alert-triangle me-2 flex-shrink-0" aria-hidden />
            <div className="d-flex flex-column">
            <span className="fw-bold d-block text-break">Посетитель находится в Черном списке</span>
            {blacklistReason && (
              <span className="fw-bold d-block text-break">{`Причина: ${blacklistReason}`}</span>
            )}
            </div>
          </div>
        )}
        <div className="card-text">
          <div className="mb-2 sigur-info-block__row">
            <strong className='sigur-info-block__label'>Этап</strong> {stateTitle}
          </div>
          <div className='sigur-info-block__row'>
            <strong className='sigur-info-block__label'>Ответственный</strong> {responsibleName}
          </div>
          {granted_access_id && (
            <div className='sigur-info-block__row'>
              <strong className='sigur-info-block__label'>Согласованные доступы</strong>
              <Link
                className='sigur-info-block__link text-decoration-none fw-medium'
                to={`/granted_accesses/${granted_access_id}/`}
              >
                {granted_access_title}
                <i className="ti ti-arrow-right ti-xs ms-1"></i>
              </Link>
            </div>
          )}
        </div>
      </Panel>
    )
  }

  renderFileBlock(isRenderDataFields = false) {
    const filesSchema = this.state.files_schema || []
    if (filesSchema.length === 0) return null
    return (
      <SigurFilesBlock
        key="files"
        ref={this.fileBlockRef}
        project_name={this.project_name}
        object_id={this.object_id}
        files_schema={filesSchema}
        files={this.state.files}
        fileUploaded={this.fileUploaded}
        getChoice={this.getChoice}
        onFileDateSaved={this.onFileDateSaved}
        canDelete={this.filesBlockCanDelete}
        viewOnly={this.filesBlockViewOnly}
        isRenderDataFields={isRenderDataFields}
        isRenderChevron={true}
        documetSetState={this.documentToState}
      />
    )
  }

  // Переопределяем renderContentRight для отображения панели информации
  renderContentRight() {
    if (!this.resolvedObjectId || this.resolvedObjectId === 0) {
      return []
    }

    return [
      this.renderInfo(),
      this.renderFileBlock(true),
      this.renderComments(),
    ]
  }

  // Переопределяем renderFormAttr: object_type с контекстом department; passport — спец. формат с пробелом; blacklist — чекбокс + модалка причины и иконка истории
  renderFormAttr(attr) {
    if (attr.name === 'object_type') {
      const data = this.state.form_data
      const form_access = this.state.form_access
      const attr_error = this.state.form_errors && this.state.form_errors[attr.name]
      const attr_message = this.state.form_messages[attr.name]
      const choice_context = { department: data.department }
      const requiredFields = this.getEffectiveRequiredFields()
      const required = Array.isArray(requiredFields) && requiredFields.includes(attr.name)
      const title = required && attr.title
        ? (
          <>
            {attr.title}{' '}
            <RequiredFieldPopover />
          </>
        )
        : attr.title
      return (
        <AttrInput
          key={attr.name}
          data_type={attr.data_type}
          choice_name={attr.choice_name}
          choices={this.props.choices}
          name={attr.name}
          choice_context={choice_context}
          title={title}
          value={data[attr.name]}
          access={form_access[attr.name]}
          message={attr_message}
          error={attr_error}
          inner_attrs={attr.inner_attrs}
          horizontal={false}
          onChange={this.formChange}
          widget={attr.widget}
        />
      )
    }

    if (attr.name === 'passport') {
      const data = this.state.form_data
      const form_access = this.state.form_access
      const access = form_access[attr.name] || 'read'
      const attr_error = this.state.form_errors && this.state.form_errors[attr.name]
      const requiredFields = this.getEffectiveRequiredFields()
      const required = Array.isArray(requiredFields) && requiredFields.includes(attr.name)
      const raw = data[attr.name]
      const digits = raw != null && raw !== '' ? String(raw).replace(/\D/g, '').slice(0, 10) : ''
      const display =
        digits.length === 0
          ? ''
          : digits.length <= 4
            ? digits
            : `${digits.slice(0, 4)} ${digits.slice(4)}`

      const readOnly = access !== 'edit'
      return (
        <div key={attr.name} className="mb-2">
          <label className="form-label">
            {attr.title}{' '}
            {required && (
              <>
                <RequiredFieldPopover />
              </>
          )}
          </label>
          {readOnly ? (
            <input
              type="text"
              className="form-control"
              value={display || '—'}
              disabled
              readOnly
            />
          ) : (
            <input
              type="text"
              className='form-control'
              inputMode="numeric"
              autoComplete="off"
              placeholder="0000 000000"
              maxLength={11}
              value={display}
              onChange={(e) => {
                const d = e.target.value.replace(/\D/g, '').slice(0, 10)
                const v = d.length === 0 ? null : d
                this.formChange('passport', v)
              }}
            />
          )}
          {attr_error && <div className="text-danger">{attr_error}</div>}
        </div>
      )
    }

    if (attr.name === 'medical_exam') {
      const data = this.state.form_data
      const form_access = this.state.form_access
      const access = form_access[attr.name] || 'read'
      const value = data[attr.name]
      const checked = value === true || value === 'true' || value === 'Да'
      const canEdit = access === 'edit'

      return (
        <div key={attr.name} className="mb-2 mt-2">
          <div className="d-flex flex-column">
            <label className="form-label mb-0 me-2">{attr.title}</label>
            {canEdit ? (
              <input
                type="checkbox"
                className="form-check-input"
                checked={checked}
                disabled={!canEdit}
                onChange={(e) => {
                  const newValue = e.target.checked

                  this.setState((prevState) => ({
                    form_data: {
                      ...prevState.form_data,
                      [attr.name]: newValue
                    }
                  }))
                }}
              />
            ) : (
              <span>{checked ? 'Да' : 'Нет'}</span>
            )}
          </div>
        </div>
      )
    }

    return super.renderFormAttr(attr)
  }

  renderContentLeftAction() {
    return super.renderContentLeftAction()
  }

  renderBlacklistModal() {
    if (!this.state.showBlacklistModal) return null
    const value = this.state.blacklistModalValue
    const reason = this.state.blacklistModalReason || ''
    const err = this.state.blacklistModalError
    const isAddingToBlacklist = Boolean(value)
    const reasonLabel = isAddingToBlacklist
      ? 'Причина занесения в черный список'
      : 'Причина исключения из черного списка'
    const reasonPlaceholder = isAddingToBlacklist
      ? 'Опишите причину занесения в черный список'
      : 'Опишите причину исключения из черного списка'
    return (
      <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Черный список</h5>
              <button type="button" className="btn-close" onClick={this.closeBlacklistModal} aria-label="Закрыть" />
            </div>
            <div className="modal-body">
              <p className="text-muted small">Поле обязательное для заполнения.</p>
              <label className="form-label">{reasonLabel}</label>
              <textarea
                className={`form-control ${err ? 'is-invalid' : ''}`}
                rows={4}
                placeholder={reasonPlaceholder}
                value={reason}
                onChange={(e) => this.setState({ blacklistModalReason: e.target.value, blacklistModalError: null })}
              />
              {err && <div className="invalid-feedback d-block">{err}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={this.closeBlacklistModal}>Отменить</button>
              <button type="button" className="btn btn-primary" onClick={this.saveBlacklistModal}>Сохранить</button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  get canRenderBlacklistModals() {
    const roles = (this.props.user && this.props.user.roles) ? this.props.user.roles : []
    const canActions = Array.isArray(roles) && roles.includes('admin')
    return Boolean(canActions || this.object_id || this.object_id !== 0)
  }

  renderContentLeftActionRightItems() {
    const items = super.renderContentLeftActionRightItems()
    if (!this.canShowDocumentActions && !this.isAdmin) return items

    const menuItems = []

    if (this.canShowDocumentActions) {
      menuItems.push(
        <li key={this.state.data_actualization_mode ? 'exit_data_actualization' : 'data_actualization'}>
          <a
            className="dropdown-item"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              if (this.state.data_actualization_mode) {
                this.exitDataActualization()
              } else {
                this.startDataActualization()
              }
            }}
          >
            {this.state.data_actualization_mode
              ? 'Выйти из режима актуализации'
              : 'Актуализировать данные'}
          </a>
        </li>
      )
    }

    if (this.isAdmin) {
      menuItems.push(
        <li key="blacklist_toggle">
          <a
            className="dropdown-item"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              this.openBlacklistModal(!this.state.form_data?.blacklist)
            }}
          >
            {this.state.form_data?.blacklist ? 'Исключить из черного списка' : 'Занести в черный список'}
          </a>
        </li>
      )
      if (Boolean(this.state.blacklistHistoryItems.length)) {
        menuItems.push(
          <li key="blacklist_history">
            <a
              className="dropdown-item"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                this.openBlacklistHistory()
              }}
            >
              Посмотреть историю изменений статусов ЧС
            </a>
          </li>
        )
      }
    }

    if (menuItems.length === 0) return items

    const dropdown = (
      <div className="dropdown" key="actions_dropdown">
        <span
          className="btn btn-outline-primary waves-effect p-2 dropdown-toggle"
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          Действия
        </span>
        <ul className="dropdown-menu dropdown-menu-start">{menuItems}</ul>
      </div>
    )

    const editAllIdx = items.findIndex((x) => x && (x.key === 'edit_all' || x.key === 'exit_edit_all'))
    const insertIdx = editAllIdx >= 0 ? editAllIdx : items.length
    return [...items.slice(0, insertIdx), dropdown, ...items.slice(insertIdx)]
  }

  renderBlacklistHistoryModal() {
    if (!this.state.showBlacklistHistoryModal) return null
    const items = this.state.blacklistHistoryItems
    const formatDate = (iso) => {
      if (!iso) return '—'
      try {
        const d = new Date(iso)
        return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      } catch (e) {
        return iso
      }
    }
    return (
      <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">История изменений</h5>
              <button type="button" className="btn-close" onClick={this.closeBlacklistHistory} aria-label="Закрыть" />
            </div>
            <div className="modal-body">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>В черном списке</th>
                    <th>Дата события</th>
                    <th>Причина</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={3} className="text-muted">Нет записей</td></tr>
                  )}
                  {items.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.in_blacklist_display || (row.in_blacklist ? 'Да' : 'Нет')}</td>
                      <td>{formatDate(row.d_event)}</td>
                      <td>{row.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={this.closeBlacklistHistory}>Закрыть</button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderModals() {
    const modals = []
    if (this.props.search_form) {
      modals.push(
        <AccessObjectsModalSearch
          key="search"
          project_name={this.project_name}
          schema={this.props.search_form}
          data={this.props.search_data}
          onSearch={this.onSearch}
          choices={this.props.choices}
        />
      )
    }

    if (this.canRenderBlacklistModals) {
      modals.push(this.renderBlacklistModal())
      modals.push(this.renderBlacklistHistoryModal())
    }
    return [...modals, ...super.renderModals()]
  }
}

export default wrapView(ModelView, (state) => stateToPropsSigur(state, PROJECT_NAME))
